/* @jsxImportSource @emotion/react */

import React from 'react'
import { css } from '@emotion/react'
import { useIntl } from 'react-intl'

import ComplianceLogos from '../../assets/compliance-logos.svg'
import { Button } from '../../components/Button/Button'
import { Section } from '../../components/Section'
import { TextLayout } from '../../components/TextLayout'
import { WebsiteText } from '../../components/WebsiteText'
import { useRoutes } from '../../routes'
import { SMALL_BREAKPOINT } from '../../utils/breakpoints'
import placeholderIllustration from './assets/placeholder.png'
import hideIcon from './assets/securityIcons/hide.svg'
import keyIcon from './assets/securityIcons/key.svg'
import lockIcon from './assets/securityIcons/lock.svg'
import logsIcon from './assets/securityIcons/logs.svg'

type SecurityItem = {
  illustration: string
  title: string
  desc: string
  extra?: React.ReactNode
}

export function SectionHomeSecurity({ hideCta }: { hideCta?: boolean } = {}) {
  const intl = useIntl()
  const routes = useRoutes()

  const content = {
    title: intl.formatMessage({
      id: 'home.security.title',
      defaultMessage: 'An infrastructure your security team can sign off on',
    }),
    cta: hideCta ? undefined : (
      <Button
        variant="secondary"
        size="default"
        href={routes.legal.security()}
        withChevron
      >
        {intl.formatMessage({
          id: 'home.security.cta',
          defaultMessage: 'Security at Slite',
        })}
      </Button>
    ),
    illustration: <img src={placeholderIllustration} alt="" />,
  }

  const items: SecurityItem[] = [
    {
      illustration: lockIcon,
      title: intl.formatMessage({
        id: 'home.security.compliance.title',
        defaultMessage: 'Compliance',
      }),
      desc: intl.formatMessage({
        id: 'home.security.compliance.desc',
        defaultMessage: 'SOC 2 Type II, HIPAA, GDPR. EU-hosted.',
      }),
      extra: (
        <img
          src={ComplianceLogos}
          alt={intl.formatMessage({
            id: 'home.security.compliance.logosAlt',
            defaultMessage: 'SOC 2, HIPAA and GDPR compliance logos',
          })}
          className="margin-top-12"
          style={{ maxWidth: 160, marginLeft: -8, opacity: 0.5 }}
        />
      ),
    },
    {
      illustration: keyIcon,
      title: intl.formatMessage({
        id: 'home.security.access.title',
        defaultMessage: 'Access',
      }),
      desc: intl.formatMessage({
        id: 'home.security.access.desc',
        defaultMessage:
          'SSO, SCIM, granular role-based permissions, reader-only seats.',
      }),
    },
    {
      illustration: hideIcon,
      title: intl.formatMessage({
        id: 'home.security.dataHandling.title',
        defaultMessage: 'Data Handling',
      }),
      desc: intl.formatMessage({
        id: 'home.security.dataHandling.desc',
        defaultMessage:
          'Your data is never used to train AI models. Enterprise customers can request our security documentation.',
      }),
    },
    {
      illustration: logsIcon,
      title: intl.formatMessage({
        id: 'home.security.auditability.title',
        defaultMessage: 'Auditability',
      }),
      desc: intl.formatMessage({
        id: 'home.security.auditability.desc',
        defaultMessage:
          'Full audit logs, version history, draft states, every write attributed.',
      }),
    },
  ]

  return (
    <Section
      background="moon-dust-lighter"
      containerVerticalPadding={120}
      className="text-align-center"
    >
      <TextLayout
        type="centered"
        title={content.title}
        titleMaxWidth={550}
        button={content.cta}
      />

      <div
        css={css`
          display: flex;
          flex-wrap: wrap;
          flex-direction: row;
          justify-content: center;
          gap: 32px;

          & > * {
            padding-left: 40px;
          }
          & > *:first-child {
            padding-left: 0;
          }
          & > *:not(:first-child) {
            border-left: 1px solid var(--alpha-shade-45);
          }

          @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
            /* 2-column grid on mobile via flex-wrap. Drop the per-item left
               padding and divider that desktop uses — the row would otherwise
               be too cramped to fit two side by side. Gap is preserved. */
            & > * {
              flex: 0 0 calc(50% - 16px) !important;
              min-width: 0 !important;
              padding: 0 !important;
            }
            & > *:not(:first-child) {
              border-left: none;
            }
          }
        `}
      >
        {items.map(({ illustration, title, desc, extra }) => (
          <div
            key={title}
            className="text-align-left"
            style={{ minWidth: '100px', flex: 1 }}
          >
            <img
              src={illustration}
              alt=""
              width={48}
              height={48}
              className="margin-bottom-16"
            />
            <WebsiteText
              variant="smallTitle"
              htmlTag="h3"
              className="margin-bottom-8 font-weight-bold shade-00"
              style={{ fontSize: 20 }}
            >
              {title}
            </WebsiteText>
            <p className="font-size-6 shade-10">{desc}</p>
            {extra}
          </div>
        ))}
      </div>
    </Section>
  )
}
